import { skipToken } from '@reduxjs/toolkit/query';
import * as Sentry from '@sentry/react';
import { motion } from 'framer-motion';
import dayjs from 'lib/dayjs';
import useUser from 'hooks/useUser';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  useCheckInMutation,
  useCheckOutMutation,
  useGetMonthlyTimeLogsByUserIdQuery,
  useGetTimeLogsByDateQuery,
} from 'redux/features/timeLogApi';
import { authApi, useGetDetailsQuery } from 'redux/features/users/authApi';
import { useUpdatePasswordMutation } from 'redux/features/users/userApi';

import Loader from 'components/ui/Loader';
import SkeletonWrapper from 'components/ui/SkeletonWrapper';
import RejectLeaveModal from 'pages/admin-leave-management/components/RejectLeaveModal';
import { useCancelEmployeeLeaveMutation } from 'redux/features/employeeLeaveApi';
import PolicyModal from 'pages/employee-rules/components/PolicyModal';
import {
  useGetEmployeeViewRulesQuery,
  useMarkRuleAsReadMutation,
} from 'redux/features/employeeRulesApi';
import { useDispatch } from 'react-redux';
import useServerTime from 'hooks/useServerTime';
import BirthdayCelebration from 'components/Celebration';
import { useGetAppreciationMessageQuery } from 'redux/features/employeeLeaveApi';
import Icon from '../../components/AppIcon';
import CommandPalette from '../../components/ui/CommandPalette';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import { formatTimeHHMMSS } from '../../utils/timeTracker';
import ChangePasswordModal from './components/ChangePasswordModal';
import CheckInOutModal from './components/CheckInOutModal';
import MonthlyLogsTable from './components/MonthlyLogsTable';
import TodayLogsWidget from './components/TodayLogsWidget';
import CheckInOutMessage from './components/CheckInOutMessage';

const DashboardOverview = () => {
  const user = useUser();
  const { data: appreciationMessage } = useGetAppreciationMessageQuery(user?.id);
  const { getSyncedTime } = useServerTime();
  const [currentStatus, setCurrentStatus] = useState('checkedOut');

  const [todaySummary, setTodaySummary] = useState({
    firstCheckIn: null,
    lastCheckOut: null,
    totalHours: 0,
    isActive: false,
  });
  const [monthlyLogs, setMonthlyLogs] = useState([]);
  const [rejectLeaveModalIsOpen, setRejectLeaveModalIsOpen] = useState(false);
  const [monthlySummary, setMonthlySummary] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState('checkIn');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [selectedYear, setSelectedYear] = useState(new Date()?.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date()?.getMonth() + 1);
  const [currentRuleIndex, setCurrentRuleIndex] = useState(0);
  const dispatch = useDispatch();

  // Helper functions for time calculation
  const calculateWorkingHours = (checkIn, checkOut) => {
    if (!checkIn || !checkOut) return 0;
    const start = new Date(checkIn);
    const end = new Date(checkOut);
    const diffInMs = end - start;
    return diffInMs / (1000 * 60 * 60); // Convert milliseconds to hours
  };

  const calculateCurrentSessionTime = (status, log) => {
    if (status !== 'checkedIn' || !log?.data?.length) {
      return '00:00:00';
    }

    const lastEntry = log?.data?.[log?.data?.length - 1];

    if (!lastEntry?.check_in || lastEntry?.check_out) {
      return '00:00:00';
    }

    const sessionHours = calculateWorkingHours(lastEntry?.check_in, currentTime);
    return formatTimeHHMMSS(sessionHours);
  };

  const calculateDynamicTotalHours = (log) => {
    if (!log?.data || log?.data?.length === 0) {
      return '00:00:00';
    }

    let totalHours = log?.data?.reduce((acc, entry) => {
      return acc + (entry?.total_hours || 0);
    }, 0);

    // Add current active session if exists
    const lastEntry = log?.data?.[log?.data?.length - 1];
    if (lastEntry?.check_in && !lastEntry?.check_out) {
      totalHours += calculateWorkingHours(lastEntry?.check_in, currentTime);
    }

    return formatTimeHHMMSS(totalHours);
  };
  const [updatePassword] = useUpdatePasswordMutation();
  const todayDate = dayjs().format('YYYY-MM-DD');
  const { data: todayLogs, isLoading: isLoadingTodayLog } = useGetTimeLogsByDateQuery(
    user?.id ? { date: todayDate, id: user.id } : skipToken,
  );
  const { data, refetch, isLoading } = useGetDetailsQuery(
    user?.id ? { role: user?.role } : skipToken,
  );

  const {
    data: monthlyLog,
    refetch: refetchTimeLog,
    isLoading: isLoadingMonthlyLog,
  } = useGetMonthlyTimeLogsByUserIdQuery(
    user?.id ? { month: selectedMonth, year: selectedYear, id: user.id } : skipToken,
  );
  const [cancelEmployeeLeave] = useCancelEmployeeLeaveMutation();
  const [checkIn, { isLoading: isCheckInSubmitting }] = useCheckInMutation();
  const [checkOut, { isLoading: isCheckOutSubmitting }] = useCheckOutMutation();

  const { data: onboardingRules } = useGetEmployeeViewRulesQuery(undefined, {
    skip: !data || data.onboarding_step !== 1,
  });
  const [markRuleAsRead, { isLoading: isMarkingAsRead }] = useMarkRuleAsReadMutation();

  const [employee, setEmployee] = useState();

  useEffect(() => {
    setEmployee(user);
    if (monthlyLog?.data) {
      setMonthlyLogs(monthlyLog.data.logs || []);
      setMonthlySummary(monthlyLog.data.summary || null);
    }
    if (todayLogs?.data?.length > 0) {
      let firstEntry = todayLogs?.data[0]?.check_in;
      let lastEntry = todayLogs?.data[todayLogs?.data?.length - 1];
      let totalHours = todayLogs?.data?.reduce((acc, log) => {
        return acc + (log?.total_hours || 0);
      }, 0);
      setTodaySummary({
        firstCheckIn: firstEntry,
        lastCheckOut: lastEntry?.check_out || null,
        totalHours,
        isActive: !lastEntry?.check_out,
      });

      const status = !lastEntry ? 'checkedOut' : lastEntry?.check_out ? 'checkedOut' : 'checkedIn';
      setCurrentStatus(status);
    }
  }, [user, todayLogs, monthlyLog]);

  useEffect(() => {
    const timeInterval = setInterval(() => {
      setCurrentTime(getSyncedTime());
    }, 1000);

    return () => {
      clearInterval(timeInterval);
    };
  }, [selectedYear, selectedMonth, employee, user, getSyncedTime, refetchTimeLog]);

  const currentSessionTime = calculateCurrentSessionTime(currentStatus, todayLogs);
  const dynamicTotalHours = calculateDynamicTotalHours(todayLogs);

  const handleCheckInOut = async () => {
    try {
      if (currentStatus === 'checkedOut') {
        if (data?.leave_requests?.length) {
          setRejectLeaveModalIsOpen(true);
          return;
        }
        const res = await checkIn({ id: user?.id }).unwrap();
        setCurrentStatus('checkedIn');
        await CheckInOutMessage(res);
      } else {
        // Open modal for check-out
        setModalType('checkOut');
        setIsModalOpen(true);
      }
    } catch (error) {
      await CheckInOutMessage({
        message: 'Failed to Check In try again',
        status: 'error',
      });
      console.error(error);
      Sentry.captureMessage('Check In failed', {
        extra: {
          id: user?.employee_id,
          email: user?.email,
          name: user?.first_name + ' ' + user?.last_name,
          role: user?.role,
        },
      });
    }
  };

  const handleModalConfirm = async (reason) => {
    try {
      const res = await checkOut({ id: user?.id, payload: { reason, date: new Date() } }).unwrap();
      setCurrentStatus('checkOut');
      setIsModalOpen(false);
      await CheckInOutMessage(res);
      dispatch(authApi.util.invalidateTags(['UserDetails']));
    } catch (error) {
      console.error(error);
      await CheckInOutMessage({
        message: 'Failed to Check Out try again',
        status: 'error',
      });
      Sentry.captureMessage('Check Out failed', {
        extra: {
          id: user?.employee_id,
          email: user?.email,
          name: user?.first_name + ' ' + user?.last_name,
          role: user?.role,
        },
      });
    }
  };

  const handleMonthChange = (year, month) => {
    setSelectedYear(year);
    setSelectedMonth(month);
  };

  const employeeName = [user?.first_name, user?.last_name].filter(Boolean).join(' ') || 'Employee';
  const today = dayjs();
  const isBirthday = user?.date_of_birth
    ? dayjs(user.date_of_birth).format('MM-DD') === today.format('MM-DD')
    : false;
  const employmentPeriod = user?.employee_periods?.find((p) => p.period_type === 'EMPLOYMENT');
  const employmentStartDate = employmentPeriod?.start_date;
  const isWorkAnniversary = employmentStartDate
    ? dayjs(employmentStartDate).format('MM-DD') === today.format('MM-DD') &&
      today.diff(dayjs(employmentStartDate), 'year') >= 1
    : false;

  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationTitle, setCelebrationTitle] = useState('');

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        setShowCelebration(false);

        setTimeout(() => {
          if (isBirthday && isWorkAnniversary) {
            setCelebrationTitle(`Happy Birthday & Work Anniversary ${employeeName}`);
            setShowCelebration(true);
          } else if (isBirthday) {
            setCelebrationTitle(`Happy Birthday ${employeeName}`);
            setShowCelebration(true);
          } else if (isWorkAnniversary) {
            setCelebrationTitle(`Happy Work Anniversary ${employeeName}`);
            setShowCelebration(true);
          }
        }, 100);
      }
    };
    if (isBirthday) {
      setCelebrationTitle(`Happy Birthday ${employeeName}`);
      setShowCelebration(true);
    } else if (isWorkAnniversary) {
      setCelebrationTitle(`Happy Work Anniversary ${employeeName}`);
      setShowCelebration(true);
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isBirthday, isWorkAnniversary, employeeName]);

  const handleChangePassword = async (payload) => {
    if (!user?.id) return;
    const res = await updatePassword({ payload, id: user.id });
    if (res?.data) {
      toast.success(res.data.message);
      refetch();
    }
    return false;
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  const handleCancelLeave = async (reason, remainAll = false) => {
    try {
      const res = await cancelEmployeeLeave({
        id: data?.leave_requests?.[0]?.id,
        reason,
        remainAll,
      }).unwrap();

      if (res) {
        toast.success(
          remainAll ? 'All remaining leave cancelled' : "Today's leave cancelled successfully",
        );

        // Update the user details and time logs
        await refetch();
        if (refetchTimeLog) await refetchTimeLog();

        // Perform the check-in directly since we know leave is now cancelled
        try {
          const res = await checkIn({ id: user?.id }).unwrap();
          setCurrentStatus('checkedIn');
          toast.success(res?.message || 'Check-In Successfully');
        } catch (checkInErr) {
          toast.error('Leave cancelled, but auto check-in failed. Please check in manually.');
          console.error('Auto check-in error:', checkInErr);
        }
      }
    } catch (error) {
      console.error('Cancel leave error:', error);
      toast.error(error?.data?.message || 'Failed to cancel leave');
    }
  };

  const getLeaveCancelInfo = (leave) => {
    const baseInfo = {
      description: 'Are you sure you want to cancel this leave request?',
      confirmLabel: 'Cancel Request',
      infoMessage: 'After canceling your leave, you will be able to check in for work.',
    };
    if (!leave) return baseInfo;

    const todayStr = dayjs().format('YYYY-MM-DD');
    const startDateStr = dayjs(leave.start_date_time).format('YYYY-MM-DD');
    const endDateStr = dayjs(leave.end_date_time).format('YYYY-MM-DD');

    const isFirstDay = todayStr === startDateStr;
    const isToday = todayStr >= startDateStr && todayStr <= endDateStr;

    if (leave.type === 'FULL_DAY_LEAVE') {
      return {
        ...baseInfo,
        description: 'Today is your full day leave. Do you want to cancel?',
      };
    }

    if (leave.type === 'SHORT_LEAVE' || leave.type === 'HALF_DAY_LEAVE') {
      const startTime = dayjs(leave.start_date_time).format('hh:mm a');
      const endTime = dayjs(leave.end_date_time).format('hh:mm a');
      const prefix = leave.type === 'SHORT_LEAVE' ? 'short time' : 'half day';
      return {
        ...baseInfo,
        description: `Your ${prefix} leave is from ${startTime} to ${endTime}. Do you want to cancel?`,
      };
    }

    // MULTIPLE_DAY_LEAVE or LONG_TIME_LEAVE
    if (isFirstDay) {
      return {
        ...baseInfo,
        description:
          'Today is the first day of your leave. Do you want to cancel the entire request?',
      };
    }

    if (isToday) {
      return {
        ...baseInfo,
        description: `You are on leave from ${startDateStr} to ${endDateStr}. Do you want to cancel all leave from now like ${todayStr} to ${endDateStr}?`,
        confirmLabel: 'Cancel Remaining',
        showRemainOptions: leave.type === 'MULTIPLE_DAY_LEAVE' || leave.type === 'LONG_TIME_LEAVE',
      };
    }

    return { ...baseInfo, showRemainOptions: false };
  };

  const cancelInfo = getLeaveCancelInfo(data?.leave_requests?.[0]);

  const handleMarkRuleAsRead = async (rule) => {
    try {
      const isLast = onboardingRules && currentRuleIndex === onboardingRules.length - 1;
      const payload = isLast ? { onboarding_step: 2 } : undefined;

      await markRuleAsRead({ id: rule.id, payload }).unwrap();

      if (!isLast) {
        setCurrentRuleIndex((prev) => prev + 1);
      } else {
        // Last rule read, hide modal and refetch
        setCurrentRuleIndex((prev) => prev + 1);
        refetch();
      }
    } catch (error) {
      toast.error('Failed to mark policy as read');
      console.error(error);
    }
  };

  const currentOnboardingRule = onboardingRules?.[currentRuleIndex];

  const isSpecialDay = isBirthday || isWorkAnniversary;
  const welcomeMessage =
    isBirthday && isWorkAnniversary
      ? 'Happy Birthday & Work Anniversary'
      : isBirthday
        ? 'Happy Birthday'
        : isWorkAnniversary
          ? 'Happy Work Anniversary'
          : 'Welcome';

  if (isLoading || !user) {
    return <Loader />;
  }
  return (
    <div className={`min-h-screen `}>
      {isSpecialDay && (
        <div
          className="fixed inset-0 -z-10"
          style={{
            backgroundImage: 'url(/assets/images/birthday-bg.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center top',
            backgroundRepeat: 'no-repeat',
            opacity: '0.9',
          }}
        />
      )}
      <Header hideSearchbar={!data?.password_updated || !isLoading} />
      <Sidebar />
      {showCelebration && (
        <BirthdayCelebration
          isOpen={showCelebration}
          title={celebrationTitle}
          onComplete={() => setShowCelebration(false)}
        />
      )}
      <CommandPalette />
      {data && data?.onboarding_step < 1 && (
        <ChangePasswordModal onConfirm={handleChangePassword} />
      )}

      <main className="lg:ml-60 pt-16">
        <motion.div
          className="max-w-7xl mx-auto px-4 sm:px-1 lg:px-1 py-4 sm:py-6 lg:py-8"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Welcome Card with Status */}
          <motion.div className="mb-3 sm:mb-2" variants={itemVariants}>
            <div className="bg-surface border border-border rounded-2xl pt-4 px-4 sm:pt-6 sm:px-6 pb-2 shadow-sm">
              <div className="flex items-center justify-between gap-4">
                {/* Left side - Welcome and Date */}
                <div className="flex-1">
                  <h1 className="text-xl sm:text-2xl font-bold text-text-primary mb-2">
                    {welcomeMessage}, {employeeName}
                  </h1>
                  <p className="text-sm sm:text-base text-text-secondary">
                    {currentTime?.toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </p>
                </div>

                {/* Right side - Total Time Today */}
                {todaySummary && (
                  <div className="px-3 py-2 bg-primary-50 border border-primary-200 rounded-lg ">
                    <div className="text-center">
                      <p className="text-xs text-primary-700  font-medium mb-1">Hours Today</p>
                      <div className="text-base sm:text-lg text-primary-800 font-mono font-bold text-success-800 tracking-wider mb-2">
                        {dynamicTotalHours}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              {/* weekly and monthly appreciation messages */}
              <h1 className="text-md mt-4 sm:text-lg font-bold text-text-secondary mb-2">
                {appreciationMessage?.msg}
              </h1>
            </div>
            {/* wish  */}
          </motion.div>

          {/* Timer and Check-in/Check-out Section */}
          <motion.div className="mb-6 sm:mb-8" variants={itemVariants}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              {/* Left Side - Timer Card */}

              <SkeletonWrapper
                width="w-full"
                height="h-72"
                className="rounded-2xl"
                isLoading={isLoadingTodayLog}
              >
                <div className="bg-surface border border-border rounded-2xl p-4 sm:p-6 shadow-sm">
                  <div className="text-center">
                    <h3 className="text-base sm:text-lg font-semibold text-text-primary mb-3 sm:mb-4">
                      Active Work Session
                    </h3>

                    {/* Current Session Timer - Only show when active */}
                    {currentStatus === 'checkedIn' ? (
                      <div className="p-4 sm:p-6 bg-success-50 border border-success-200 rounded-xl">
                        <p className="text-xs sm:text-sm text-success-700 font-medium mb-2">
                          Current Session
                        </p>
                        <div className="text-2xl sm:text-3xl lg:text-4xl font-mono font-bold text-success-800 tracking-wider mb-2">
                          {currentSessionTime}
                        </div>
                        <p className="text-xs text-success-600">HH:MM:SS</p>
                      </div>
                    ) : (
                      <div className="p-4 sm:p-6 bg-secondary-50 border border-secondary-200 rounded-xl">
                        <p className="text-xs sm:text-sm text-secondary-700 font-medium mb-2">
                          No Active Session
                        </p>
                        <div className="text-2xl sm:text-3xl lg:text-4xl font-mono font-bold text-secondary-600 tracking-wider mb-2">
                          00:00:00
                        </div>
                        <p className="text-xs text-secondary-500">HH:MM:SS</p>
                      </div>
                    )}

                    {/* Status Indicator */}
                    <div
                      className={`inline-flex items-center space-x-2 px-3 sm:px-4 py-2 rounded-full mt-3 sm:mt-4 ${
                        currentStatus === 'checkedIn'
                          ? 'bg-success-100 text-success-700'
                          : 'bg-secondary-100 text-secondary-700'
                      }`}
                    >
                      <div
                        className={`w-2 h-2 rounded-full ${
                          currentStatus === 'checkedIn'
                            ? 'bg-success animate-pulse'
                            : 'bg-secondary-400'
                        }`}
                      ></div>
                      <span className="text-xs sm:text-sm font-medium">
                        {currentStatus === 'checkedIn' ? 'Active Work Session' : 'Not Working'}
                      </span>
                    </div>
                  </div>
                </div>
              </SkeletonWrapper>
              {/* Right Side - Check-in/Check-out Card */}
              <SkeletonWrapper
                width="w-full"
                height="h-72"
                className="rounded-2xl"
                isLoading={isLoadingTodayLog}
              >
                <div className="bg-surface border border-border rounded-2xl p-4 sm:p-6 shadow-sm">
                  <div className="text-center">
                    <div className="mb-4 sm:mb-6">
                      <Icon
                        name={currentStatus === 'checkedIn' ? 'Play' : 'Square'}
                        size={40}
                        color={
                          currentStatus === 'checkedIn'
                            ? 'var(--color-success)'
                            : 'var(--color-primary)'
                        }
                        className="mx-auto mb-3 sm:mb-4"
                      />
                      <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-text-primary mb-2">
                        {currentStatus === 'checkedIn'
                          ? 'Currently Checked In'
                          : 'Ready to Start Work?'}
                      </h2>
                      <p className="text-sm sm:text-base text-text-secondary">
                        {currentStatus === 'checkedIn'
                          ? 'You are currently tracking your work time. Click below to check out.'
                          : 'Click the button below to start tracking your work time for today.'}
                      </p>
                    </div>

                    {/* Check-in/Check-out Button */}
                    <button
                      disabled={isCheckInSubmitting || isCheckOutSubmitting}
                      onClick={handleCheckInOut}
                      className={`w-full max-w-xs mx-auto disabled:opacity-50 py-3 sm:py-4 px-6 sm:px-8 rounded-xl font-semibold text-base sm:text-lg transition-all duration-200 flex items-center justify-center space-x-2 sm:space-x-3 ${
                        currentStatus === 'checkedIn'
                          ? 'bg-warning text-white hover:bg-warning-600 shadow-lg hover:shadow-warning-200'
                          : 'bg-success text-white hover:bg-success-600 shadow-lg hover:shadow-success-200'
                      }`}
                    >
                      <>
                        {isCheckInSubmitting ? (
                          <Icon name="Loader" className="animate-spin" size={20} />
                        ) : (
                          <Icon
                            name={currentStatus === 'checkedIn' ? 'Square' : 'Play'}
                            size={20}
                          />
                        )}

                        <span>{currentStatus === 'checkedIn' ? 'Check Out' : 'Check In'}</span>
                      </>
                    </button>
                  </div>
                </div>
              </SkeletonWrapper>
            </div>
          </motion.div>

          {/* Today's Logs Widget */}
          <motion.div className="mb-6 sm:mb-8" variants={itemVariants}>
            <SkeletonWrapper
              width="w-full"
              height="h-[400px]"
              className="mb-7"
              isLoading={isLoadingTodayLog}
            >
              <TodayLogsWidget todayLogs={todayLogs} summary={todaySummary} />
            </SkeletonWrapper>
          </motion.div>

          {/* Monthly Time Logs Table */}
          <motion.div variants={itemVariants}>
            <SkeletonWrapper
              width="w-full"
              height="h-[600px]"
              className="mb-7"
              isLoading={isLoadingMonthlyLog}
            >
              <MonthlyLogsTable
                monthlyLogs={monthlyLogs}
                summary={monthlySummary}
                onMonthChange={handleMonthChange}
                userId={user?.employee_id}
              />
            </SkeletonWrapper>
          </motion.div>
        </motion.div>
      </main>
      <RejectLeaveModal
        isOpen={rejectLeaveModalIsOpen}
        onClose={() => setRejectLeaveModalIsOpen(false)}
        leaveDetails={{ ...data?.leave_requests?.[0], user: data }}
        type="cancel"
        onConfirm={handleCancelLeave}
        description={cancelInfo.description}
        confirmLabel={cancelInfo.confirmLabel}
        showSecondaryButton={false}
        infoMessage={cancelInfo.infoMessage}
        showRemainOptions={cancelInfo.showRemainOptions}
      />

      {/* Check-in/Check-out Modal */}
      <CheckInOutModal
        isSubmitting={isCheckOutSubmitting}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
        }}
        onConfirm={handleModalConfirm}
        type={modalType}
      />

      <PolicyModal
        open={!!currentOnboardingRule && data?.onboarding_step === 1}
        policy={currentOnboardingRule}
        onMarkAsRead={handleMarkRuleAsRead}
        isLoading={isMarkingAsRead}
        isLast={onboardingRules && currentRuleIndex === onboardingRules.length - 1}
        pendingCount={onboardingRules ? onboardingRules.length - currentRuleIndex : 0}
      />
    </div>
  );
};

export default DashboardOverview;
